import React, { useEffect, useRef, useState } from 'react';
import { Users, Calendar, Zap, Globe, ArrowRight, ChevronDown, CreditCard, Bot, Mail, BarChart3, ScanLine, Settings2, Check, X, Leaf } from 'lucide-react';

/* ─── Animations CSS injectées globalement ─── */
const GlobalStyles = () => (
  <style>{`
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse-slow {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.05); }
    }
    @keyframes bounce-subtle {
      0%, 100% { transform: translateY(0); }
      50%       { transform: translateY(8px); }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position:  200% center; }
    }

    .animate-fade-up   { animation: fadeUp   0.7s ease both; }
    .animate-fade-in   { animation: fadeIn   0.6s ease both; }
    .animate-slide-down{ animation: slideDown 0.5s ease both; }
    .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }

    .delay-100 { animation-delay: 0.1s; }
    .delay-200 { animation-delay: 0.2s; }
    .delay-300 { animation-delay: 0.3s; }
    .delay-400 { animation-delay: 0.4s; }
    .delay-500 { animation-delay: 0.5s; }
    .delay-600 { animation-delay: 0.6s; }

    /* Cards hover */
    .feature-card {
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }
    .feature-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.12);
    }

    /* Boutons */
    .btn-primary {
      transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    }
    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(37, 99, 235, 0.4);
    }
    .btn-secondary {
      transition: transform 0.2s ease, background 0.2s ease;
    }
    .btn-secondary:hover {
      transform: translateY(-2px);
      background: rgba(255,255,255,0.15);
    }

    /* Header sticky glassmorphism */
    .header-glass {
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      transition: background 0.3s ease, box-shadow 0.3s ease;
    }

    /* Gradient texte animé */
    .gradient-text {
      background: linear-gradient(135deg, #60a5fa, #a78bfa, #60a5fa);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: shimmer 3s linear infinite;
    }

    /* Scroll reveal */
    .reveal {
      opacity: 0;
      transform: translateY(28px);
      transition: opacity 0.7s ease, transform 0.7s ease;
    }
    .reveal.visible {
      opacity: 1;
      transform: translateY(0);
    }

    /* Separator animé */
    .separator {
      width: 0;
      height: 3px;
      background: linear-gradient(90deg, #2563eb, #7c3aed);
      border-radius: 9999px;
      transition: width 0.8s ease;
      margin: 0 auto;
    }
    .separator.visible { width: 64px; }
  `}</style>
);

/* ─── Hook: scroll reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ─── HubSpot form ─── */
const HubSpotForm = () => {
  useEffect(() => {
    const scriptId = 'hubspot-forms-v2';
    const loadForm = () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: '6330329',
          formId: 'b9ec5526-ed9a-4fc8-bd81-acbf2ce1ef40',
          region: 'na1',
          target: '#hubspot-form-container',
        });
      }
    };
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.charset = 'utf-8';
      script.type = 'text/javascript';
      script.src = '//js.hsforms.net/forms/embed/v2.js';
      script.onload = loadForm;
      document.body.appendChild(script);
    } else {
      loadForm();
    }
  }, []);
  return <div id="hubspot-form-container" className="mt-8 max-w-xl mx-auto" />;
};

/* ─── Feature card ─── */
const FeatureCard = ({ icon: Icon, color, bg, title, description, delay }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal feature-card bg-white p-8 rounded-2xl shadow-md text-center delay-${delay}`}>
      <div className={`w-16 h-16 ${bg} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
        <Icon className={color} size={30} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
};

/* ─── Section title ─── */
const SectionTitle = ({ title, subtitle }) => {
  const ref = useReveal();
  const sepRef = useReveal();
  return (
    <div className="text-center mb-14">
      <h2 ref={ref} className="reveal text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{title}</h2>
      <div ref={sepRef} className="reveal separator mb-5" />
      {subtitle && <p className="text-gray-500 text-lg max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
};

/* ─── Pricing card ─── */
const PricingCard = ({ plan, price, badge, highlight, ftStyle, subtitle, features, excluded, delay }) => {
  const ref = useReveal();
  const cardClass = highlight
    ? 'bg-gradient-to-b from-blue-600 to-indigo-700 text-white shadow-2xl ring-4 ring-blue-300 ring-opacity-40'
    : ftStyle
      ? 'bg-white shadow-md'
      : 'bg-white shadow-md';
  return (
    <div ref={ref} className={`reveal feature-card flex flex-col rounded-2xl p-8 delay-${delay} ${cardClass}`}
      style={ftStyle ? { borderWidth: '2px', borderStyle: 'solid', borderColor: '#da303c' } : {}}>
      {badge && (
        <div className={`self-start text-xs font-bold px-3 py-1 rounded-full mb-4 ${highlight ? 'bg-white text-blue-700' : 'bg-red-50 text-red-700'}`}
          style={ftStyle ? { color: '#da303c', backgroundColor: '#fef2f2' } : {}}>
          {badge}
        </div>
      )}
      <h3 className={`text-xl font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`}>{plan}</h3>
      <div className={`text-5xl font-extrabold mt-3 mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`}>
        €{price}
      </div>
      <p className={`text-sm mb-6 pb-6 border-b ${highlight ? 'text-blue-200 border-blue-400 border-opacity-40' : 'text-gray-400 border-gray-100'}`}>
        {subtitle}
      </p>
      <ul className="flex-1 space-y-3 mb-8">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${highlight ? 'text-blue-100' : 'text-gray-600'}`}>
            <Check size={15} className={`mt-0.5 flex-shrink-0 ${highlight ? 'text-white' : 'text-emerald-500'}`} />
            {f}
          </li>
        ))}
        {excluded && excluded.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <X size={15} className="mt-0.5 flex-shrink-0 text-gray-300" />
            <span className="line-through">{f}</span>
          </li>
        ))}
      </ul>
      <a href="#contact"
        className={`text-center py-3 px-6 rounded-xl font-semibold text-sm transition-all ${highlight ? 'bg-white text-blue-700 hover:bg-blue-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        style={ftStyle ? { background: '#da303c', color: '#fff' } : {}}>
        Get started
      </a>
    </div>
  );
};

/* ─── Add-on table ─── */
const AddOnTable = ({ title, accent, rows }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal bg-white rounded-2xl shadow-md overflow-hidden border ${accent}`}>
      <div className="px-6 py-4 font-bold text-sm bg-blue-50 text-blue-700"
        style={accent.includes('red') ? { backgroundColor: '#fef2f2', color: '#da303c' } : {}}>{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
            <th className="px-6 py-3 text-left font-semibold">Participants</th>
            <th className="px-6 py-3 text-right font-semibold">Add-on price</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([range, price], i) => (
            <tr key={i} className={`border-t border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50 bg-opacity-50'}`}>
              <td className="px-6 py-3 text-gray-600">{range}</td>
              <td className="px-6 py-3 text-right font-semibold text-gray-900">{price}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─── Add-on modal ─── */
const AddonModal = ({ onClose, rows }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={onClose}>
    <div className="absolute inset-0 bg-black bg-opacity-50" style={{ backdropFilter: 'blur(4px)' }} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
        <h3 className="text-lg font-bold text-gray-900">Participant add-ons</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors rounded-lg p-1 hover:bg-gray-100">
          <X size={20} />
        </button>
      </div>
      <p className="px-6 pt-4 pb-2 text-sm text-gray-500">Base includes 100 participants. Additional participants are billed per tier, all plans included.</p>
      <AddOnTable title="All plans" accent="border-blue-100" rows={rows} />
      <div className="px-6 py-4 text-xs text-gray-400 border-t border-gray-50">Prices excl. VAT. For 1,000+ participants, contact us for a custom quote.</div>
    </div>
  </div>
);

/* ─── Page principale ─── */
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const features = [
    { icon: Users,      color: 'text-blue-600',   bg: 'bg-blue-50',   title: 'Smart Matching',       description: 'Algorithm based on your offers and requests for highly relevant one-to-one matches.',  delay: 100 },
    { icon: Calendar,   color: 'text-purple-600', bg: 'bg-purple-50', title: 'Auto Scheduling',      description: 'Automatic assignment of time slots and meeting tables, zero manual work.',             delay: 200 },
    { icon: Zap,        color: 'text-emerald-600',bg: 'bg-emerald-50',title: 'Simple & Fast',        description: 'Sign up in minutes. Instant matching, no setup required.',                             delay: 300 },
    { icon: Globe,      color: 'text-orange-600', bg: 'bg-orange-50', title: 'Multilingual',         description: 'Interface available in French, English and German.',                                   delay: 400 },
  ];

  const advancedFeatures = [
    { icon: CreditCard, color: 'text-indigo-600', bg: 'bg-indigo-50', title: 'Badge Generation',    description: 'Automatic creation of digital and printable badges with QR code, vCard and Apple Wallet pass.', delay: 100 },
    { icon: Bot,        color: 'text-cyan-600',   bg: 'bg-cyan-50',   title: 'Meetings Automation', description: 'Intelligent meeting suggestions sent automatically based on participant profiles.',             delay: 200 },
    { icon: Mail,       color: 'text-rose-600',   bg: 'bg-rose-50',   title: 'Email Automation',    description: 'Automated emails for invitations, reminders and meeting confirmations.',                       delay: 300 },
    { icon: BarChart3,  color: 'text-amber-600',  bg: 'bg-amber-50',  title: 'Organizer Statistics',description: 'Real-time dashboard with attendance, check-in rates and meeting metrics.',                     delay: 400 },
    { icon: ScanLine,   color: 'text-teal-600',   bg: 'bg-teal-50',   title: 'Badge Scanning',      description: 'Mobile QR scanner for instant check-in at the entrance, with live counter.',                  delay: 500 },
    { icon: Settings2,  color: 'text-slate-600',  bg: 'bg-slate-50',  title: 'Event Management',    description: 'Multi-event support with sub-events, custom participant types and time slot configuration.',   delay: 600 },
  ];

  const plans = [
    {
      plan: 'Matching Event', price: '799', delay: 100,
      subtitle: 'per event · excl. VAT · base 100 participants',
      features: [
        'Full web matchmaking platform',
        'Meeting scheduling with vCard QR',
        'Automated emails (confirmation & reminders)',
        'Participant CSV export',
        'Email support Mon–Fri',
      ],
      excluded: ['iOS & Android mobile app'],
    },
    {
      plan: 'Matching Event Pro', price: '1,499', delay: 200,
      badge: '⭐ Most popular', highlight: true,
      subtitle: 'per event · excl. VAT · 400 participants',
      features: [
        'Everything in Event',
        'iOS & Android mobile app',
        'Advanced organizer dashboard',
        'Post-event PDF report',
        '1h onboarding & setup included',
        'Priority chat support',
      ],
    },
    {
      plan: 'La French Tech Community', price: '699', delay: 300,
      badge: '🐓 La French Tech exclusive', ftStyle: true,
      subtitle: 'per event · excl. VAT · base 100 participants',
      features: [
        'Event Plan platform access',
        'Pre-configured French Tech template',
        'Cross-chapter network directory',
        'Reserved for verified French Tech Community',
        'Early access to new features',
      ],
    },
  ];

  const addonStandard = [
    ['Base', '100 included'],
    ['101–200', '+€90'],
    ['201–300', '+€150'],
    ['301–500', '+€200'],
    ['501–700', '+€290'],
    ['701–999', '+€390'],
    ['1,000+', 'Custom quote'],
  ];

  const [showAddonModal, setShowAddonModal] = useState(false);
  const enterpriseRef = useReveal();
  const ctaRef = useReveal();
  const eventsRef = useReveal();
  const contactTitleRef = useReveal();

  return (
    <>
      <GlobalStyles />
      <div className="min-h-screen bg-white font-sans">

        {/* ── Header sticky logo ── */}
        <header
          className="header-glass fixed top-0 left-0 right-0 z-50"
          style={{
            background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
            boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
            pointerEvents: scrolled ? 'auto' : 'none',
            opacity: scrolled ? 1 : 0,
            transition: 'opacity 0.3s ease, background 0.3s ease, box-shadow 0.3s ease',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-center">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="cursor-pointer"
            >
              <img
                src="https://matchmaking.mobivent.fr/logo-2025-Mobivent-400.png"
                alt="Mobivent — retour en haut"
                className="h-10"
                onError={(e) => e.target.style.display = 'none'}
              />
            </a>
          </div>
        </header>

        {/* ── Hero (plein écran, logo inclus) ── */}
        <section
          className="relative w-full flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: "url('/header.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Overlay gradient */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, rgba(0,0,0,0.65) 0%, rgba(15,30,80,0.55) 100%)' }}
          />

          {/* Cercles décoratifs flous */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full"
            style={{ background: 'rgba(99,102,241,0.15)', filter: 'blur(60px)', animation: 'pulse-slow 5s ease-in-out infinite' }} />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full"
            style={{ background: 'rgba(37,99,235,0.12)', filter: 'blur(80px)', animation: 'pulse-slow 7s ease-in-out infinite 1s' }} />

          <div className="relative z-10 text-center px-4 sm:px-8 max-w-4xl mx-auto">

            {/* Logo centré sur le hero */}
            <img
              src="https://matchmaking.mobivent.fr/logo-2025-Mobivent-400-dark.png"
              alt="Mobivent"
              className="h-16 sm:h-20 mx-auto mb-8 animate-fade-in drop-shadow-lg px-6 sm:px-0"
              onError={(e) => e.target.style.display = 'none'}
            />

            <div className="inline-block bg-white bg-opacity-10 border border-white border-opacity-20 text-white text-sm font-medium px-4 py-2 rounded-full mb-8 animate-fade-in delay-100"
              style={{ backdropFilter: 'blur(8px)' }}>
              ✦ Intelligent B2B Networking Platform
            </div>

            <h2 className="text-4xl sm:text-6xl font-extrabold text-white mb-6 leading-tight animate-fade-up delay-200"
              style={{ letterSpacing: '-0.03em' }}>
              B2B Matchmaking Platform
              <br />
              <span className="gradient-text">for Professional Events</span>
            </h2>

            <p className="text-lg sm:text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-up delay-300">
              Connect with the right partners at your events.
              Our intelligent algorithm analyzes your needs and suggests the best matches.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-up delay-400">
              <a href="#contact"
                className="btn-primary bg-blue-600 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg flex items-center justify-center gap-2">
                Ask for a demo <ArrowRight size={18} />
              </a>
              <a href="#events"
                className="btn-secondary border-2 border-white border-opacity-60 text-white px-8 py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2"
                style={{ backdropFilter: 'blur(8px)', background: 'rgba(255,255,255,0.08)' }}>
                Discover our events
              </a>
            </div>

            {/* Flèche scroll */}
            <div className="mt-16 flex justify-center animate-bounce-subtle">
              <ChevronDown className="text-white opacity-60" size={32} />
            </div>
          </div>
        </section>

        {/* ── Features ── */}
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
          <SectionTitle
            title="Everything you need for perfect meetings"
            subtitle="Our platform handles the entire matchmaking lifecycle, from invitations to scheduled meetings."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {features.map((f) => <FeatureCard key={f.title} {...f} />)}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancedFeatures.map((f) => <FeatureCard key={f.title} {...f} />)}
          </div>
        </section>

        {/* ── Demo video ── */}
        <section className="w-full bg-black" style={{ aspectRatio: '16/9' }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ display: 'block', pointerEvents: 'none' }}
          >
            <source src="https://matchmaking.mobivent.fr/Demo-Matchmaking.mp4" type="video/mp4" />
          </video>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" className="bg-gray-50 py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <SectionTitle
              title="Simple, transparent pricing"
              subtitle="Per event, no subscription. All plans include the full platform. Base includes 100 participants."
            />

            {/* Plans */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start mb-8">
              {plans.map((p) => <PricingCard key={p.plan} {...p} />)}
            </div>

            {/* Addon button */}
            <div className="flex justify-center mb-14">
              <button
                onClick={() => setShowAddonModal(true)}
                className="text-sm text-blue-600 font-medium border border-blue-200 px-5 py-2.5 rounded-xl hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                View participant add-on pricing
                <ArrowRight size={14} />
              </button>
            </div>

            {/* Enterprise callout */}
            <div ref={enterpriseRef} className="reveal bg-white rounded-2xl border border-gray-200 shadow-sm p-8 flex flex-col sm:flex-row items-center justify-between gap-6 mb-16">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-1">Running multiple events or expecting 1,000+ participants?</h4>
                <p className="text-gray-500 text-sm max-w-2xl">Organizations hosting recurring events benefit from custom pricing and dedicated support. Get in touch for a tailored quote.</p>
              </div>
              <a href="#contact" className="flex-shrink-0 btn-primary bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 shadow">
                Contact us <ArrowRight size={16} />
              </a>
            </div>

          </div>
        </section>

        {/* ── Add-on modal ── */}
        {showAddonModal && <AddonModal rows={addonStandard} onClose={() => setShowAddonModal(false)} />}

        {/* ── Events ── */}
        <section id="events" className="bg-gray-50 py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <SectionTitle
              title="Available Events"
              subtitle="Select your event to access the matchmaking platform"
            />
            <div ref={eventsRef} className="reveal bg-white rounded-2xl shadow-lg p-12 text-center max-w-2xl mx-auto border border-gray-100">
              <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Calendar className="text-blue-600" size={40} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Access via direct link
              </h3>
              <p className="text-gray-500 leading-relaxed">
                Each event has its own dedicated URL.
                Check your invitation email or contact the event organizer to get your personalized access link.
              </p>
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="py-24 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 50%, #7c3aed 100%)' }}>
          <div className="absolute inset-0"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)' }} />
          <div ref={ctaRef} className="reveal relative z-10 max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-6" style={{ letterSpacing: '-0.02em' }}>
              Ready to optimize your networking?
            </h2>
            <p className="text-lg text-blue-100 mb-10 opacity-90">
              Join thousands of professionals who trust our platform
            </p>
            <a href="#contact"
              className="btn-primary inline-flex items-center gap-2 bg-white text-blue-700 px-8 py-4 rounded-xl text-base font-bold shadow-xl">
              Request a demo <ArrowRight size={18} />
            </a>
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" className="max-w-3xl mx-auto px-4 sm:px-8 py-24">
          <div className="text-center">
            <h2 ref={contactTitleRef} className="reveal text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ask for a demo and get an attractive price
            </h2>
            <p className="text-gray-500 text-lg mb-2">Fill out the form and our team will get back to you.</p>
            <HubSpotForm />
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="bg-gray-900 text-gray-400 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-sm">
            <div className="flex flex-col gap-3">
              <a href="https://www.mobivent.fr" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">© Mobivent Solutions - All rights reserved</a>
              <a
                href="https://lafrenchtechduesseldorf.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src="/Logo_FT_Dusseldorf_Blanc.png"
                  alt="French Tech Düsseldorf"
                  className="h-8"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <span>La French Tech Düsseldorf Member</span>
              </a>
            </div>
            <a
              href="https://www.mobivent.fr/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </footer>

      </div>
    </>
  );
};

export default HomePage;
